import type { InferenceModelResult } from '../shared-camera/types';

export type ScanMachineState =
  | 'priming'
  | 'scanning'
  | 'paused'
  | 'reviewing'
  | 'error';

export interface BinBbox {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/**
 * Symbology kind for a decoded marker. Used by the overlay to branch
 * color/label per detector and by the dispatch pipeline to skip
 * fiducials (which don't dispatch inferences this iteration).
 */
export type DecodedSymbolKind = 'qr' | 'apriltag' | 'aruco' | 'text';

export interface DecodedSymbol {
  text: string;
  bbox: BinBbox;
  orientation: number;
  /** Detector that produced this symbol. Defaults to 'qr' for legacy call sites. */
  kind: DecodedSymbolKind;
  /** Active dictionary, populated for fiducial symbols (e.g. 'apriltag_36h11'). */
  dictionary?: string;
}

/**
 * Active detection format selected by the user. Exactly one detector
 * runs per frame. Scope narrowed mid-implementation to QR + ArUco 4x4 (50)
 * — AprilTag and the larger ArUco grids were dropped after the user
 * decided to focus on a single fiducial format for the feasibility
 * comparison. fiducialDecoder still has the dictionary table entries
 * for the dropped formats; widening the union here surfaces them again.
 */
export type ActiveFormat = 'qr' | 'aruco_4x4_1000' | 'text';

export const ACTIVE_FORMATS: readonly ActiveFormat[] = ['qr', 'aruco_4x4_1000', 'text'] as const;

export function isActiveFormat(v: unknown): v is ActiveFormat {
  return typeof v === 'string' && (ACTIVE_FORMATS as readonly string[]).includes(v);
}

export interface ContinuousScanResponse {
  inference_run_id: string;
  qr_identifier: string;
  capture_mode: 'continuous_scan' | 'single_shot';
  client_idempotency_key: string | null;
  bin_bbox: BinBbox | null;
  item: { id: string; qr_identifier: string; name: string; par_level: number | null };
  prompt_version: string;
  captured_at: string;
  results: InferenceModelResult[];
}

export type ScanResultStatus = 'pending' | 'ok' | 'error' | 'aborted';

export interface ScanResultEntry {
  idempotencyKey: string;
  qrValue: string;
  snapshotBlob: Blob;
  capturedAt: string;
  status: ScanResultStatus;
  errorMessage?: string;
  response?: ContinuousScanResponse;
  operatorTruth?: number;
}

export interface PersistedSession {
  sessionId: string;
  lastUpdated: number;
  dedupe: string[];
  results: PersistedResult[];
}

export interface PersistedResult {
  idempotencyKey: string;
  qrValue: string;
  capturedAt: string;
  status: ScanResultStatus;
  errorMessage?: string;
  response?: ContinuousScanResponse;
  operatorTruth?: number;
}
